/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import CampaignModel, { CampaignType } from 'cio/models/campaign';
import { action } from '@ember/object';
import { CampaignRestartModes } from 'cio/models/campaign';
import { inject as service } from '@ember/service';
import AuthService from 'cio/services/auth';
import {
  SECONDS_IN_MINUTE,
  SECONDS_IN_HOUR,
  SECONDS_IN_DAY,
  TIMEFRAME_UNITS,
  findRoundUnit,
  roundTimeframe,
} from 'cio/utils/round-timeframe-units';
import pluralize from 'cio/utils/pluralize';

interface CampaignsTriggerSetupFrequencyControlsArgs {
  campaign: CampaignModel;
  type: CampaignType;
  disabled: boolean;
  restartMode: CampaignRestartModes;
  restartMinInterval: number;
  onUpdateRestartMode: (restartMode: string) => unknown;
  onUpdateInterval: (interval: number) => unknown;
}

const NOUN_PLACEHOLDER = 'NOUN_PLACEHOLDER';
const INTERVAL_PLACEHOLDER = 'INTERVAL_PLACEHOLDER';
const FREQUENCY_OPTION_NOUNS = {
  [CampaignType.Form]: 'form submission',
  [CampaignType.Transactional]: 'event',
};

enum TimeFrameUnitType {
  minute = 'minute',
  hour = 'hour',
  day = 'day',
  week = 'week',
}

type BaseFrequencyTypeOption = {
  interval: number;
  restartMode?: CampaignRestartModes;
  value: string;
  label: string;
  description: string;
  summary: string;
  timeSelectorLabel?: string;
  timeFrameUnits?: TimeFrameUnitType[];
  minSeconds?: number;
  disabled?: boolean;
};

enum EventFormValue {
  OnEvery = 'on_every',
  OnceEver = 'once_ever',
  OnceWithin = 'once_within',
}

type EventFrequencyTypeOption = BaseFrequencyTypeOption & {
  value: EventFormValue;
};

type SegmentFrequencyTypeOption = BaseFrequencyTypeOption & {
  value: CampaignRestartModes;
};

type FrequencyTypeOption = EventFrequencyTypeOption | SegmentFrequencyTypeOption | BaseFrequencyTypeOption;

export const SegmentFrequencyOptions: FrequencyTypeOption[] = [
  {
    interval: 0, // default for segments matches default for events/form which is "on every: interval 0"
    restartMode: CampaignRestartModes.None,
    value: CampaignRestartModes.None,
    label: 'One time',
    description: `People will only enter this campaign the first time they meet the trigger conditions. They cannot enter the campaign again.`,
    summary:
      'People can only enter the workflow one time, even if they match the segment or filter conditions again in the future.',
  },
  {
    interval: SECONDS_IN_DAY,
    restartMode: CampaignRestartModes.Rematch,
    value: CampaignRestartModes.Rematch,
    label: 'Every re-match',
    description:
      'People will enter this campaign every time they meet the trigger conditions. They must stop matching the trigger conditions before they can re-match the conditions and trigger the campaign again.',
    timeSelectorLabel: 'What is the minimum time that should pass before the person can trigger the campaign again?',
    timeFrameUnits: [TimeFrameUnitType.minute, TimeFrameUnitType.hour, TimeFrameUnitType.day, TimeFrameUnitType.week],
    minSeconds: SECONDS_IN_MINUTE,
    summary: `People will re-enter the campaign if they stop matching the segment or filter conditions and then rematch them. They can only enter one time every ${INTERVAL_PLACEHOLDER}.`,
  },
  {
    interval: SECONDS_IN_DAY,
    restartMode: CampaignRestartModes.Interval,
    value: CampaignRestartModes.Interval,
    label: 'At fixed intervals',
    description:
      'As long as trigger conditions are met, people will enter this campaign every time a set interval passes between the start of a journey and the next one.',
    tooltip:
      'People will not re-enter this campaign if they still have an active journey. If they stop matching the trigger conditions then re-match, they will immediately trigger this campaign, which could mean less time passes than the fixed interval below.',
    timeSelectorLabel: 'How much time should pass between entry and re-entry?',
    timeFrameUnits: [TimeFrameUnitType.day, TimeFrameUnitType.week],
    minSeconds: SECONDS_IN_DAY,
    summary: `People will automatically re-enter the campaign every ${INTERVAL_PLACEHOLDER} as long as the segment or filter conditions are still met and they aren't already in the workflow.`,
  },
].map((opt) => Object.freeze(opt));

export const EventAndFormFrequencyOptions: FrequencyTypeOption[] = [
  {
    interval: -1,
    value: EventFormValue.OnceEver,
    label: 'One time',
    description: `This campaign will only trigger the first time the ${NOUN_PLACEHOLDER} is received for each person, after the campaign is published.`,
    summary: 'People will only enter the campaign the first time they match the trigger conditions.',
  },
  {
    interval: 0,
    value: EventFormValue.OnEvery,
    label: `On every ${NOUN_PLACEHOLDER}`,
    description: `This campaign will trigger for every ${NOUN_PLACEHOLDER} is received for each person, after the campaign is published.`,
    summary: 'People will enter the campaign every time they match the trigger conditions.',
  },
  {
    interval: SECONDS_IN_DAY,
    value: EventFormValue.OnceWithin,
    label: 'Once within a time period',
    description: `This campaign will only trigger the first time the ${NOUN_PLACEHOLDER} is received during a specific time period, after the campaign is published.`,
    timeSelectorLabel: 'How much time should pass before people are eligible to enter again?',
    timeFrameUnits: [TimeFrameUnitType.hour, TimeFrameUnitType.day, TimeFrameUnitType.week],
    minSeconds: SECONDS_IN_HOUR,
    summary: `People will enter the campaign the first time they match the trigger conditions within a ${INTERVAL_PLACEHOLDER} time period.`,
  },
].map((opt) => Object.freeze(opt));

export const ObjectAndRelationshipFrequencyOptions: BaseFrequencyTypeOption[] = [
  Object.freeze({
    interval: SECONDS_IN_DAY,
    restartMode: CampaignRestartModes.Rematch,
    value: CampaignRestartModes.Rematch,
    disabled: true,
    label: 'Every re-match',
    description: 'Campaign will trigger every time the conditions are met.',
    summary: 'People will enter the campaign every time the trigger conditions are met.',
  }),
];

function getIntervalString(restartMinInterval: number, timeFrameUnits: TimeFrameUnitType[] | undefined) {
  if (restartMinInterval == null || restartMinInterval <= 0 || (timeFrameUnits ?? []).length === 0) {
    return '';
  }
  let seconds = roundTimeframe(restartMinInterval);
  let unit = findRoundUnit(
    seconds,
    TIMEFRAME_UNITS.filter((unit) => timeFrameUnits?.includes(unit.value as TimeFrameUnitType)),
  );
  let count = Math.ceil(seconds / unit.seconds);
  return `${count} ${pluralize(count, unit.value)}`;
}

export function getSelectedFrequencyOption(
  type: CampaignType,
  frequencyTypeOptions: FrequencyTypeOption[],
  restartMode: CampaignRestartModes,
  restartMinInterval: number,
) {
  switch (type) {
    case CampaignType.Behavioral: {
      let option = frequencyTypeOptions.find((o) => restartMode === o.value);
      if (option != null) {
        let interval = '';
        if (restartMode !== CampaignRestartModes.None && restartMinInterval > 0) {
          interval = getIntervalString(restartMinInterval, option.timeFrameUnits);
        }
        return {
          ...option,
          summary: option.summary.replace(INTERVAL_PLACEHOLDER, interval),
        };
      }
      return null;
    }

    case CampaignType.Transactional:
    case CampaignType.Form: {
      let option = frequencyTypeOptions.find((o) => restartMinInterval === o.interval) ?? frequencyTypeOptions[2];
      if (option != null) {
        let noun = FREQUENCY_OPTION_NOUNS[type];
        let interval = '';
        if (restartMinInterval > 0) {
          interval = getIntervalString(restartMinInterval, option.timeFrameUnits);
        }
        return {
          ...option,
          label: option.label.replace(NOUN_PLACEHOLDER, noun),
          description: option.description.replace(NOUN_PLACEHOLDER, noun),
          summary: option.summary.replace(INTERVAL_PLACEHOLDER, interval),
        };
      }
      return null;
    }
    case CampaignType.Object:
    case CampaignType.ObjectRelationship: {
      return ObjectAndRelationshipFrequencyOptions[0];
    }
    default: {
      return null;
    }
  }
}

export default class CampaignsTriggerSetupFrequencyControls extends Component<CampaignsTriggerSetupFrequencyControlsArgs> {
  @service auth!: AuthService;

  get frequencyTypeOptions(): FrequencyTypeOption[] {
    switch (this.args.type) {
      case CampaignType.Behavioral: {
        return SegmentFrequencyOptions;
      }
      case CampaignType.Transactional:
      case CampaignType.Form: {
        let noun = FREQUENCY_OPTION_NOUNS[this.args.type];
        return EventAndFormFrequencyOptions.map((o) => ({
          ...o,
          label: o.label.replace(NOUN_PLACEHOLDER, noun),
          description: o.description.replace(NOUN_PLACEHOLDER, noun),
        }));
      }
      case CampaignType.Object:
      case CampaignType.ObjectRelationship: {
        return ObjectAndRelationshipFrequencyOptions;
      }
      default: {
        return [];
      }
    }
  }

  get selectedFrequency() {
    return getSelectedFrequencyOption(
      this.args.type,
      this.frequencyTypeOptions,
      this.args.restartMode,
      this.args.restartMinInterval,
    );
  }

  get selectedFrequencyPeriodSeconds() {
    return this.args.restartMinInterval > 0 ? this.args.restartMinInterval : null;
  }

  get showConditionalTooltip() {
    return (
      this.args.disabled &&
      [CampaignType.Behavioral, CampaignType.Object, CampaignType.ObjectRelationship].includes(this.args.type)
    );
  }

  get conditionTooltipContent() {
    let { type } = this.args;
    if (type === CampaignType.Behavioral) {
      return "Trigger frequency can only be changed when there aren't any filters set. Remove your filters, and then you'll be able to change the frequency";
    }
    if (type === CampaignType.Object || type === CampaignType.ObjectRelationship) {
      return 'For now, campaigns with custom object triggers only support the "every re-match" frequency. Stay tuned for more frequency options in upcoming updates.';
    }

    return '';
  }

  @action
  setFrequencyType(option: FrequencyTypeOption) {
    if (this.selectedFrequency?.value !== option.value) {
      if (option.restartMode != null) {
        this.args.onUpdateRestartMode(option.restartMode);
      }
      this.args.onUpdateInterval(option.interval);
    }
  }
}
